import type { InvoiceAlert } from '@features/invoices/api/types/invoice-alert.types';

import { useInvoiceAlerts } from '@features/invoices/hooks/use-invoice-alerts/useInvoiceAlerts';
import * as React from 'react';

import type {
  InvoiceAlertsContextValue,
  InvoiceAlertsProviderProps,
} from './InvoiceAlertsContext.types';

const InvoiceAlertsContext = React.createContext<
  InvoiceAlertsContextValue | undefined
>(undefined);

const InvoiceAlertsProvider = React.memo(
  ({ children }: Readonly<InvoiceAlertsProviderProps>): React.ReactElement => {
    const { data: invoiceAlerts = [] } = useInvoiceAlerts();
    const [selectedAlert, setSelectedAlert] =
      React.useState<InvoiceAlert | null>(null);

    const isAlertPanelOpen = selectedAlert !== null;

    const openAlertPanel = React.useCallback((alert: InvoiceAlert): void => {
      setSelectedAlert(alert);
    }, []);

    const closeAlertPanel = React.useCallback((): void => {
      setSelectedAlert(null);
    }, []);

    const contextValue: InvoiceAlertsContextValue = React.useMemo(
      () => ({
        invoiceAlerts,
        isAlertPanelOpen,
        selectedAlert,
        openAlertPanel,
        closeAlertPanel,
      }),
      [
        invoiceAlerts,
        isAlertPanelOpen,
        selectedAlert,
        openAlertPanel,
        closeAlertPanel,
      ]
    );

    return (
      <InvoiceAlertsContext value={contextValue}>
        {children}
      </InvoiceAlertsContext>
    );
  }
);

InvoiceAlertsProvider.displayName = 'InvoiceAlertsProvider';

export { InvoiceAlertsProvider, InvoiceAlertsContext };
